import { useEffect, useMemo } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { FieldErrors, UseFormReturn, useForm } from "react-hook-form";
import { PERMISSION } from "Constants";
import { useAllowedTo, useCurrentOrganization, useDialog } from "Hooks";
import { apiErrorToast } from "Utils";
import {
  useSamlConfigQuery,
  useUpdateSamlConfigMutation,
} from "../services/sso.api";
import { SAMLFormData, UpdateSamlConfigResponse } from "../types";
import { SAMLFormSchema } from "../utils";

type UseSamlSSOConfiguration = {
  errors: FieldErrors<SAMLFormData>;
  onSubmit: () => void;
  methods: UseFormReturn<SAMLFormData>;
  isDirty: boolean;
  isConfirmSaveOpen: boolean;
  openConfirmSaveDialog: () => void;
  closeConfirmSaveDialog: () => void;
  handleUpdateSSOConfiguration: () => void;
  isLoading: boolean;
};

const useSamlSSOConfigForm = (
  sso_saml_configuration_id?: string | null,
): UseSamlSSOConfiguration => {
  const [updateSamlConfig] = useUpdateSamlConfigMutation();

  // this additional permissions check helps skip api calls when someone tries to access a
  // page they don't have natural access to
  const isAdmin = useAllowedTo({
    permissions: [PERMISSION.SETUP_SSO],
  });

  const confirmSaveDialog = useDialog();

  const currentOrganization = useCurrentOrganization();

  const { data: samlConfiguration, isLoading: isSamlConfigLoading } =
    useSamlConfigQuery(
      {
        configId: sso_saml_configuration_id ?? "",
      },
      {
        skip: !currentOrganization || !sso_saml_configuration_id || !isAdmin,
      },
    );

  const handleUpdateSSOConfiguration = async () => {
    try {
      if (currentOrganization) {
        // we have to cast here
        // form fields are giving us "" for empty fields
        // so we can use the form schema to cast the form data
        // to get the correct null values for nullable fields
        const formData = SAMLFormSchema.cast(getValues()) as SAMLFormData;

        if (formData) {
          await updateSamlConfig({
            samlSsoConfiguration: {
              organization_id: currentOrganization.id,
              ...formData,
            },
          })
            .unwrap()
            .then((response: UpdateSamlConfigResponse) => {
              window.location.href = response.saml_login_url;
            });
        }
      }
    } catch (error) {
      apiErrorToast(error, "Could not update SSO configuration");
    }
  };

  const onSubmit = () => {
    confirmSaveDialog.open();
  };

  const defaultValues = useMemo((): SAMLFormData => {
    return {
      metadata_url: samlConfiguration?.metadata_url ?? "",
      org_role_mappings: samlConfiguration?.org_role_mappings ?? [
        {
          mapping: "",
          org_role: "OrganizationAdmin",
        },
      ],
      user_email_mapping: samlConfiguration?.user_email_mapping ?? "",
      user_first_name_mapping:
        samlConfiguration?.user_first_name_mapping ?? null,
      user_groups_mapping: samlConfiguration?.user_groups_mapping ?? "",
      user_last_name_mapping: samlConfiguration?.user_last_name_mapping ?? null,
    };
  }, [
    samlConfiguration?.metadata_url,
    samlConfiguration?.user_first_name_mapping,
    samlConfiguration?.org_role_mappings,
    samlConfiguration?.user_email_mapping,
    samlConfiguration?.user_groups_mapping,
    samlConfiguration?.user_last_name_mapping,
  ]);

  const methods = useForm<SAMLFormData>({
    resolver: yupResolver(SAMLFormSchema),
    defaultValues,
  });

  const {
    formState: { errors, isDirty },
    reset,
    handleSubmit,
    watch,
    getValues,
    clearErrors,
  } = methods;

  // This will watch the form and reset validation errors when org role mappings change.
  useEffect(() => {
    const subscription = watch((_, { name }) => {
      if (name?.startsWith("org_role_mappings")) {
        clearErrors("org_role_mappings");
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, clearErrors]);

  // we need this because our form conditionally renders depending on the chosen protocol.
  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  return {
    onSubmit: handleSubmit(onSubmit),
    errors,
    methods,
    isDirty: isDirty,
    isConfirmSaveOpen: confirmSaveDialog.isOpen,
    openConfirmSaveDialog: confirmSaveDialog.open,
    closeConfirmSaveDialog: confirmSaveDialog.close,
    handleUpdateSSOConfiguration,
    isLoading: isSamlConfigLoading,
  };
};

export default useSamlSSOConfigForm;
